import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { authenticatedFetch } from '../../utils/apiClient';
import { track } from '../../utils/analytics';
import './ChatMessage.css';

const ChatMessage = ({ message, authToken, API_BASE_URL, conversationId }) => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [planCreated, setPlanCreated] = useState(false);
  const isCreatingPlanRef = useRef(false); // Ref to prevent multiple simultaneous calls

  // Use a ref to manage the audio queue and state to avoid re-renders
  const audioQueueRef = useRef([]);
  const currentAudioRef = useRef(null);
  const isPlayingRef = useRef(false);

  const playChunksProgressively = async (chunks, totalTextLength) => {
    // Reset state
    audioQueueRef.current = [];
    isPlayingRef.current = true;
    let nextChunkIndex = 0;

    // Function to fetch the next audio chunk and add it to the queue
    const fetchAndQueueChunk = async (chunkIndex) => {
      if (chunkIndex >= chunks.length) return; // All chunks fetched

      try {
        const chunk = chunks[chunkIndex];
        const response = await fetch(`${API_BASE_URL}/api/chat/tts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            text: chunk,
            chunk_index: chunkIndex,
            total_chunks: chunks.length
          })
        });

        if (response.ok) {
          const audioBlob = await response.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          audioQueueRef.current.push({ audio: new Audio(audioUrl), url: audioUrl });
        } else {
          console.error(`Failed to generate audio for chunk ${chunkIndex}`);
        }
      } catch (error) {
        console.error(`Error generating audio for chunk ${chunkIndex}:`, error);
      }
    };

    // Function to play the next audio in the queue
    const playNext = () => {
      if (!isPlayingRef.current || audioQueueRef.current.length === 0) {
        // If queue is empty but more chunks are expected, wait briefly
        if (nextChunkIndex < chunks.length) {
          setTimeout(playNext, 100);
        } else {
          setIsPlaying(false);
          isPlayingRef.current = false;
        }
        return;
      }

      const { audio, url } = audioQueueRef.current.shift();
      currentAudioRef.current = audio;
      setCurrentAudio(audio);

      audio.onended = () => {
        URL.revokeObjectURL(url);
        // Pre-fetch the next chunk while the current one plays
        if (nextChunkIndex < chunks.length) {
          fetchAndQueueChunk(nextChunkIndex++);
        }
        // Add a short pause for more natural speech cadence
        setTimeout(() => {
          playNext();
        }, 250); // 250ms pause
      };

      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        URL.revokeObjectURL(url);
        playNext();
      };
      
      audio.play().catch(e => {
        console.error('Audio play failed:', e);
        playNext();
      });
    };

    // Start by fetching the first two chunks to build a small buffer
    fetchAndQueueChunk(nextChunkIndex++);
    if (nextChunkIndex < chunks.length) {
      fetchAndQueueChunk(nextChunkIndex++);
    }

    // Start the playback loop
    playNext();
    
    // Track usage
    track('chat_tts_used', {
      type: 'server_tts_chunked',
      text_length: totalTextLength,
      chunks: chunks.length,
      success: true
    });
  };

  const stopAudio = () => {
    isPlayingRef.current = false;
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setCurrentAudio(null);
    audioQueueRef.current = [];
  }

  const speakText = async (text) => {
    if (isPlayingRef.current) {
      stopAudio();
      return;
    }

    setIsPlaying(true);
    isPlayingRef.current = true;

    // Try chunked server TTS first
    try {
      // Step 1: Get text chunks
      const chunkedResponse = await fetch(`${API_BASE_URL}/api/chat/tts/chunked`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });

      if (chunkedResponse.ok) {
        const { chunks, total_chunks } = await chunkedResponse.json();
        
        if (chunks.length === 0) {
          throw new Error('No chunks to process');
        }

        // Step 2: Process chunks progressively
        await playChunksProgressively(chunks, text.length);
        return;
      } else {
        throw new Error('Chunked TTS failed');
      }
    } catch (error) {
      console.log('Chunked TTS unavailable, trying single request:', error.message);
      
      // Fallback to single TTS request
      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/tts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ text })
        });

        if (response.ok) {
          const audioBlob = await response.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          
          setCurrentAudio(audio);
          currentAudioRef.current = audio;
          
          audio.onended = () => {
            stopAudio();
            URL.revokeObjectURL(audioUrl);
          };
          
          audio.onerror = () => {
            stopAudio();
            URL.revokeObjectURL(audioUrl);
            console.error('Audio playback error');
          };
          
          await audio.play();
          
          // Track successful server TTS usage
          track('chat_tts_used', {
            type: 'server_tts_single',
            text_length: text.length,
            success: true
          });
          
          return;
        } else {
          // Check if it's a fallback response
          const errorData = await response.json();
          if (errorData.fallback) {
            console.log('Falling back to browser TTS');
            // Fall through to browser TTS
          } else {
            throw new Error('TTS service error');
          }
        }
      } catch (error) {
        console.log('Server TTS unavailable, using browser TTS:', error.message);
        // Fall through to browser TTS
      }
    }

    // Fallback to browser TTS
    if ('speechSynthesis' in window) {
      // Clean up text for speech (remove markdown and special characters)
      const cleanText = text
        .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown
        .replace(/\*([^*]+)\*/g, '$1') // Remove italic markdown
        .replace(/[-*]\s+/g, '') // Remove list bullets
        .replace(/\n+/g, ' ') // Replace newlines with spaces
        .trim();

      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      // Configure speech settings
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1.0;
      utterance.volume = 0.8;
      
      // Set voice to a more natural one if available
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Natural') || 
        voice.name.includes('Enhanced') ||
        voice.name.includes('Premium')
      ) || voices.find(voice => voice.lang.startsWith('en'));
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      utterance.onend = () => {
        setIsPlaying(false);
      };

      utterance.onerror = () => {
        setIsPlaying(false);
        console.error('Speech synthesis error');
      };

      // Track browser TTS fallback usage
      track('chat_tts_used', {
        type: 'browser_fallback',
        text_length: cleanText.length,
        success: true
      });

      window.speechSynthesis.speak(utterance);
    } else {
      setIsPlaying(false);
      console.warn('No TTS available');
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return null;

    // Split text into lines to handle formatting
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let inList = false;
    let listItems = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join(' ').trim();
        if (paragraphText) {
          elements.push(
            <p key={elements.length} className="chat-paragraph">
              {formatInlineText(paragraphText)}
            </p>
          );
        }
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} className="chat-list">
            {listItems.map((item, index) => (
              <li key={index} className="chat-list-item">
                {formatInlineText(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    const formatInlineText = (text) => {
      if (!text) return text;

      // Convert markdown links [text](url) - supports both absolute (http/https) and relative (/path) URLs
      const linkRegex = /\[([^\]]+)\]\(((?:https?:\/\/[^\s)]+|\/[^\s)]+))\)/g;
      const partsWithLinks = [];
      let lastIndex = 0;
      let match;
      while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          partsWithLinks.push(text.slice(lastIndex, match.index));
        }
        partsWithLinks.push({ linkText: match[1], href: match[2] });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        partsWithLinks.push(text.slice(lastIndex));
      }

      // Now handle bold inside each non-link segment
      const renderBold = (segment) => {
        const parts = segment.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, idx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            const boldText = part.slice(2, -2);
            return <strong key={`b-${idx}`}>{boldText}</strong>;
          }
          return part;
        });
      };

      return partsWithLinks.map((p, i) => {
        if (typeof p === 'string') {
          return <React.Fragment key={`t-${i}`}>{renderBold(p)}</React.Fragment>;
        } else {
          // Check if it's a relative URL (internal link)
          const isRelativeUrl = p.href.startsWith('/');
          return (
            <a 
              key={`a-${i}`} 
              href={p.href} 
              target={isRelativeUrl ? '_self' : '_blank'} 
              rel={isRelativeUrl ? undefined : 'noreferrer'}
            >
              {p.linkText}
            </a>
          );
        }
      });
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Empty line - flush current content and add spacing
      if (!trimmedLine) {
        if (inList) {
          flushList();
        } else {
          flushParagraph();
        }
        return;
      }

      // List item (starts with - or *)
      if (trimmedLine.match(/^[-*]\s+/)) {
        if (!inList) {
          flushParagraph();
          inList = true;
        }
        const listText = trimmedLine.replace(/^[-*]\s+/, '');
        listItems.push(listText);
        return;
      }

      // If we were in a list but this line isn't a list item, flush the list
      if (inList && !trimmedLine.match(/^[-*]\s+/)) {
        flushList();
      }

      // Regular paragraph text
      if (trimmedLine) {
        currentParagraph.push(trimmedLine);
      }
    });

    // Flush any remaining content
    if (inList) {
      flushList();
    } else {
      flushParagraph();
    }

    return elements.length > 0 ? elements : <span>{text}</span>;
  };

  if (message.is_thinking) {
    return <div className="thinking">Pulc is thinking...</div>;
  }

  if (message.content === '[Audio message sent]') {
    return (
      <div className="audio-message">
        <span className="audio-indicator">🎵 Audio message</span>
      </div>
    );
  }

  const detectTimetable = (text) => {
    if (!text) return false;
    
    // Pattern for day headers with optional time info
    const dayPattern = /\*\*[Mm]on(day)?|[Tt]ue(sday)?|[Ww]ed(nesday)?|[Tt]hu(rsday)?|[Ff]ri(day)?|[Ss]at(urday)?|[Ss]un(day)?/;
    
    // Pattern for time entries
    const timePattern = /\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}/;
    
    // Check if text contains both day headers and time entries
    const hasDay = dayPattern.test(text);
    const hasTime = timePattern.test(text);
    
    // Also check for markdown formatted day headers
    const hasMarkdownDay = /\*\*[^*]+\*\*/.test(text);
    
    // Consider it a timetable if we have both day indicators and time entries
    return (hasDay || hasMarkdownDay) && hasTime;
  };

  const handleAddToCalendar = async (overwriteConflicts = false) => {
    if (!conversationId || !message.id || planCreated || isCreatingPlanRef.current) return;
    
    isCreatingPlanRef.current = true;
    setIsCreatingPlan(true);
    
    try {
      // Ensure we're sending plain data only (no React elements or circular references)
      const requestData = {
        conversation_id: Number(conversationId),
        message_id: Number(message.id),
        timetable_text: String(message.content || ''),
        overwrite_conflicts: Boolean(overwriteConflicts)
      };
      
      const response = await authenticatedFetch(`${API_BASE_URL}/api/study-plans/from-chat`, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check if this is an existing plan (from a previous message in this conversation)
        if (data.already_exists && !overwriteConflicts) {
          isCreatingPlanRef.current = false;
          setIsCreatingPlan(false);
          
          // Offer to update with this new timetable
          const shouldUpdate = window.confirm(
            `A study plan already exists for this conversation (${data.sessions_created} sessions).\n\n` +
            `Would you like to replace it with this new timetable?`
          );
          
          if (shouldUpdate) {
            // Retry with overwrite to replace the old plan
            await handleAddToCalendar(true);
          }
          return;
        }
        
        setPlanCreated(true);
        isCreatingPlanRef.current = false;
        setIsCreatingPlan(false);
        track('study_plan_created_from_chat', {
          conversation_id: conversationId,
          message_id: message.id,
          sessions_created: data.sessions_created,
          conflicts_resolved: data.conflicts_resolved || 0
        });
        
        // Show success message with option to navigate
        const conflictsMsg = data.conflicts_resolved > 0 
          ? `\n\n${data.conflicts_resolved} conflicting session(s) were replaced.`
          : '';
        const shouldNavigate = window.confirm(
          `Study plan created successfully! ${data.sessions_created} sessions added to your calendar.${conflictsMsg}\n\nWould you like to view your calendar now?`
        );
        
        if (shouldNavigate) {
          navigate('/study-plans');
        }
      } else if (response.status === 409) {
        // Conflict detected - show dialog asking user
        isCreatingPlanRef.current = false;
        setIsCreatingPlan(false); // Reset loading state before showing dialog
        const data = await response.json();
        const conflicts = data.conflicts || [];
        
        let conflictMessage = `Found ${conflicts.length} conflicting session(s):\n\n`;
        conflicts.forEach((conflict, idx) => {
          const newTime = new Date(conflict.new_session.start_time).toLocaleString();
          const existingTitle = conflict.existing_session.title;
          const existingTime = new Date(conflict.existing_session.start_time).toLocaleString();
          conflictMessage += `${idx + 1}. "${conflict.new_session.subject}" (${newTime})\n   Conflicts with: "${existingTitle}" (${existingTime})\n\n`;
        });
        
        conflictMessage += 'Would you like to replace the conflicting sessions with the new ones?';
        
        const shouldOverwrite = window.confirm(conflictMessage);
        
        if (shouldOverwrite) {
          // Retry with overwrite_conflicts=true
          await handleAddToCalendar(true);
        }
        // If user cancels, just return (don't set planCreated)
        return;
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to create study plan';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating study plan:', error);
      // Safely extract error message, avoiding circular references
      let errorMessage = 'Failed to create study plan';
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      alert(`Failed to create study plan: ${errorMessage}`);
      isCreatingPlanRef.current = false;
      setIsCreatingPlan(false);
    }
  };

  const hasTimetable = detectTimetable(message.content);
  const isAssistantMessage = message.sender_type === 'assistant';
  const hasTTS = ('speechSynthesis' in window) || authToken; // Show if browser TTS or server TTS available

  return (
    <div className="chat-message-content">
      {isAssistantMessage && hasTTS && (
        <div className="message-controls">
          <button 
            className={`tts-button ${isPlaying ? 'playing' : ''}`}
            onClick={() => speakText(message.content)}
            title={isPlaying ? "Stop speaking" : "Read message aloud"}
          >
            {isPlaying ? '⏹️' : '🔊'}
          </button>
        </div>
      )}
      {renderMarkdown(message.content)}
      {isAssistantMessage && hasTimetable && !planCreated && (
        <div className="timetable-action" style={{ marginTop: '12px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f7fafc' }}>
          <button
            className="add-to-calendar-btn"
            onClick={handleAddToCalendar}
            disabled={isCreatingPlan || !conversationId}
            style={{
              padding: '8px 16px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isCreatingPlan ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isCreatingPlan ? 0.6 : 1
            }}
          >
            {isCreatingPlan ? 'Creating...' : '📅 Add to Calendar'}
          </button>
        </div>
      )}
      {isAssistantMessage && hasTimetable && planCreated && (
        <div className="timetable-action" style={{ marginTop: '12px', padding: '8px', border: '1px solid #d1fae5', borderRadius: '8px', backgroundColor: '#f0fdf4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#059669' }}>
            <span>✅ Added to calendar</span>
            <button
              onClick={() => navigate('/study-plans')}
              style={{
                padding: '4px 12px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                marginLeft: '8px'
              }}
            >
              View Calendar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessage; 